import React from 'react';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';

import {Button, Select, Loading, Label, PlainButton} from 'ui-kit';

import * as styles from './EditPlanForm.module.css';

const formatMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format;

const COLLECTION_TYPES = {
  manual: 'MANUAL',
  card: 'CARD',
};

function EditPlanForm({
  availablePlans,
  onSubmit,
  onCancel,
  memberId,
  hasPaymentSource,
  sourceLast4,
  mutate,
  openPaymentModal,
}) {
  const [selectedPlanId, setSelectedPlanId] = React.useState(
    availablePlans[0].id.toString(),
  );
  const [collectionMethod, setCollectionMethod] = React.useState(
    hasPaymentSource ? COLLECTION_TYPES.card : COLLECTION_TYPES.manual,
  );
  const [loading, setLoading] = React.useState(false);

  const selectedPlan = availablePlans.find(
    plan => plan.id.toString() === selectedPlanId,
  );

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);

    try {
      await mutate({
        variables: {
          input: {
            memberV2Id: memberId,
            membershipPlanId: selectedPlanId,
            paymentType: collectionMethod,
          },
        },
      });

      await onSubmit();
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  return (
    <div className={styles.Container}>
      <form onSubmit={handleSubmit} method="post">
        <Select
          label="Select a plan"
          size="small"
          value={selectedPlanId}
          onChange={event => setSelectedPlanId(event.target.value)}
        >
          {availablePlans.map(({id, title, priceCents}) => (
            <option value={id} key={id}>
              {title} – {formatMoney(priceCents / 100)}
            </option>
          ))}
        </Select>

        <div className={styles.PaymentMethod}>
          <Label>Select a payment option</Label>

          {hasPaymentSource ? null : (
            <p className={styles.AddPaymentText}>
              <small>
                There is no payment source on file for this membership.{' '}
                <PlainButton onClick={openPaymentModal}>
                  <small>Add a card</small>
                </PlainButton>
              </small>
            </p>
          )}

          {hasPaymentSource ? (
            <Label fontWeight="regular">
              <input
                type="radio"
                name="collectionType"
                value={COLLECTION_TYPES.card}
                disabled={!hasPaymentSource}
                checked={collectionMethod === COLLECTION_TYPES.card}
                onChange={() => setCollectionMethod(COLLECTION_TYPES.card)}
                className={styles.Radio}
              />
              Card on file{' '}
              {hasPaymentSource ? (
                <span>
                  <small>(Ending in {sourceLast4})</small>
                </span>
              ) : null}
            </Label>
          ) : null}

          <Label fontWeight="regular">
            <input
              type="radio"
              name="collectionType"
              value={COLLECTION_TYPES.manual}
              checked={collectionMethod === COLLECTION_TYPES.manual}
              onChange={() => setCollectionMethod(COLLECTION_TYPES.manual)}
              className={styles.Radio}
            />
            Manually collect
          </Label>
        </div>

        <p className={styles.DisclaimerText}>
          {collectionMethod === COLLECTION_TYPES.card ? (
            <small>
              When you click Submit, the membership's saved payment method will
              be charged {formatMoney(selectedPlan.priceCents / 100)}{' '}
              immediately and will be set up to auto-renew yearly.
            </small>
          ) : (
            <small>
              By saving with manual collection, you verify that you have or will
              receive {formatMoney(selectedPlan.priceCents / 100)} outside of
              Plum. This subscription <strong>will not</strong> auto-renew.
            </small>
          )}
        </p>

        <div className={styles.ButtonBar}>
          <Button type="submit" size="small" disabled={loading}>
            {loading ? <Loading immediate /> : 'Submit'}
          </Button>
          <Button
            type="button"
            size="small"
            color="light"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default compose(
  graphql(
    gql`
      mutation CreateMembershipPlanSubscription(
        $input: CreateMembershipPlanSubscriptionInput!
      ) {
        createMembershipPlanSubscription(input: $input) {
          errors {
            message
            path
          }
          membershipPlanSubscription {
            id
          }
        }
      }
    `,
  ),
)(EditPlanForm);
